import { ReactElement, Ref, useImperativeHandle, useState } from 'react'
import { LayoutChangeEvent, StyleSheet, View, ViewProps } from 'react-native'
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch'

import { Button } from '../button'
import { Spacing } from '../../style'

const styles = StyleSheet.create({
  container: {
    overflow: 'hidden',
  },
  content: {
    flex: 1,
  },
  controls: {
    position: 'absolute',
    bottom: Spacing.XS + Spacing.XXS,
    left: Spacing.S,
    right: 0,
    alignItems: 'flex-start',
  },
  button: {
    marginVertical: Spacing.XXS,
  },
})

export interface ZoomValue {
  scale: number
  x: number
  y: number
}

export interface Props extends ViewProps {
  minScale?: number
  maxScale?: number
  disabled?: boolean
  initialZoom?: Partial<ZoomValue>
  onZoom?: (value: ZoomValue) => void
  onContentSizeChange?: (width: number, height: number) => void
  overlay?: ReactElement
  test_ref?: Ref<any>
  rotation?: 0 | 90 | 180 | 270
}

const SCALE_MIN = 1
const SCALE_STEPS = 10

const ZoomView = (props: Props) => {
  const {
    disabled,
    initialZoom,
    onZoom,
    onContentSizeChange,
    test_ref,
    rotation,
    children,
    overlay,
    style,
    ...otherProps
  } = props
  const [currentZoom, setCurrentZoom] = useState<ZoomValue>({
    scale: initialZoom?.scale || props.minScale || SCALE_MIN,
    x: initialZoom?.x || 0,
    y: initialZoom?.y || 0,
  })
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  const [contentSize, setContentSize] = useState({ width: 0, height: 0 })
  const [initialZoomed, setInitialZoomed] = useState(false)

  const minScale =
    props.minScale || rotation === 90 || rotation === 270
      ? Math.min(containerSize.width / contentSize.height, containerSize.height / contentSize.width)
      : Math.min(containerSize.width / contentSize.width, containerSize.height / contentSize.height) || SCALE_MIN

  const maxScale = minScale * SCALE_STEPS

  useImperativeHandle(test_ref, () => ({
    layoutContainer: (event: LayoutChangeEvent) => {
      layoutContainer(event)
    },
    layoutContent: (event: LayoutChangeEvent) => {
      layoutContent(event)
    },
  }))

  function layoutContainer(event: LayoutChangeEvent) {
    setContainerSize({ width: event.nativeEvent.layout.width, height: event.nativeEvent.layout.height })
  }
  function layoutContent(event: LayoutChangeEvent) {
    setContentSize({
      width: event.nativeEvent.layout.width,
      height: event.nativeEvent.layout.height,
    })
    onContentSizeChange?.(event.nativeEvent.layout.width, event.nativeEvent.layout.height)
  }

  function updateZoom(value: ZoomValue) {
    setCurrentZoom(value)
    onZoom?.(value)
  }

  function handleZoom(ref: ReactZoomPanPinchRef) {
    const { scale, positionX: x, positionY: y } = ref.state
    updateZoom({ scale, x, y })
  }

  function handleZoomIn(setTransform: (x: number, y: number, scale: number) => void) {
    const newScale = Math.min(maxScale, currentZoom.scale * Math.exp(0.5))
    const center = {
      x: currentZoom?.x + (currentZoom?.scale * contentSize.width) / 2 || containerSize.width / 2,
      y: currentZoom?.y + (currentZoom?.scale * contentSize.height) / 2 || containerSize.height / 2,
    }
    const x = center.x - (newScale * contentSize.width) / 2
    const y = center.y - (newScale * contentSize.height) / 2
    setTransform(x, y, newScale)
    updateZoom({ scale: newScale, x, y })
  }

  function handleZoomOut(setTransform: (x: number, y: number, scale: number) => void) {
    const newScale = Math.max(minScale, currentZoom.scale / Math.exp(0.5))
    const center = {
      x: currentZoom?.x + (currentZoom?.scale * contentSize.width) / 2 || containerSize.width / 2,
      y: currentZoom?.y + (currentZoom?.scale * contentSize.height) / 2 || containerSize.height / 2,
    }
    const x = center.x - (newScale * contentSize.width) / 2
    const y = center.y - (newScale * contentSize.height) / 2
    setTransform(x, y, newScale)
    updateZoom({ scale: newScale, x, y })
  }

  function handleResetTransform(setTransform: (x: number, y: number, scale: number) => void) {
    const x = (containerSize.width - contentSize.width * minScale) / 2
    const y = (containerSize.height - contentSize.height * minScale) / 2
    setTransform(x, y, minScale)
    updateZoom({ scale: minScale, x, y })
  }
  return (
    <TransformWrapper
      onZoomStop={handleZoom}
      onPanningStop={handleZoom}
      initialScale={initialZoom?.scale}
      initialPositionX={initialZoom?.x}
      initialPositionY={initialZoom?.y}
      minScale={minScale}
      maxScale={maxScale}
      panning={{ disabled }}
      pinch={{ disabled }}
      wheel={{ disabled }}
      doubleClick={{ disabled }}
    >
      {({ setTransform }) => {
        if (!initialZoomed && containerSize.width && containerSize.height && contentSize.width && contentSize.height) {
          const x = initialZoom?.x || (containerSize.width - contentSize.width * minScale) / 2
          const y = initialZoom?.y || (containerSize.height - contentSize.height * minScale) / 2
          const scale = initialZoom?.scale || minScale

          if (initialZoom?.scale !== scale || initialZoom?.x !== x || initialZoom?.y !== y) {
            setTransform(x, y, scale, 0)
            updateZoom({ scale, x: initialZoom?.x || 0, y: initialZoom?.y || 0 })
          }
          setInitialZoomed(true)
        }
        return (
          <View style={[styles.container, style]} {...otherProps}>
            <View style={styles.content} onLayout={layoutContainer}>
              <TransformComponent
                wrapperStyle={{
                  width: 'initial' as const,
                  height: containerSize.height,
                  overflow: 'visible' as const,
                }}
              >
                <View
                  testID="ZoomView.Content"
                  onLayout={layoutContent}
                  style={!!rotation && { transform: [{ rotate: `${rotation}deg` }] }}
                >
                  {children}
                </View>
              </TransformComponent>
            </View>
            {!disabled && (
              <View style={styles.controls} pointerEvents="box-none">
                <Button
                  left="plus"
                  onPress={() => handleZoomIn(setTransform)}
                  light
                  style={styles.button}
                  size="S"
                  testID="ZoomView.ZoomIn"
                />
                <Button
                  left="minus"
                  onPress={() => handleZoomOut(setTransform)}
                  light
                  style={styles.button}
                  size="S"
                  testID="ZoomView.ZoomOut"
                />
                <Button
                  left="maximize"
                  onPress={() => handleResetTransform(setTransform)}
                  light
                  style={styles.button}
                  size="S"
                  testID="ZoomView.ZoomReset"
                />
              </View>
            )}
            {overlay}
          </View>
        )
      }}
    </TransformWrapper>
  )
}

export default ZoomView
